import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from "yup";
import { Modal, Form, Popover, OverlayTrigger } from 'react-bootstrap';
import { BiHide, BiShow } from 'react-icons/bi';
import Loader from '../../components/loader/Loader';
// import { adminPswUpdateTodo } from '../../redux/slice/AdminManagement';
import { ToastContainer, toast } from 'react-toastify';
import SaveButton from '../../components/buttons/SaveButton';
import DatePicker from '../../components/input_fields/DatePicker';
import dayjs from 'dayjs';
import { profileDetails } from '../../redux/slice/ProfileDetails';
import { useNavigate } from 'react-router-dom';
import { updateProfile } from '../../redux/slice/UpdateProfile';
import PasswordStrengthBar from '../../components/input_fields/PasswordStrengthBar';
import PhoneInput, {
  formatPhoneNumber,
  formatPhoneNumberIntl,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  parsePhoneNumber
} from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { Info } from 'lucide-react';
import labels from 'react-phone-number-input/locale/en'
import Text from '../../components/input_fields/Text';


export default function UpdateProfile(props) {
  const { show, setShow } = props;
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [spinner, setSpinner] = useState(false)
  const [newPswShow, setNewPswShow] = useState(false);
  const [currentDate, setCurrentDate] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const navigate = useNavigate();
  const userData = useSelector((state) => state?.profileDetails?.data?.[0]);

  const dob_days = userData?.last_dob_update
    ? dayjs(currentDate).diff(dayjs(userData.last_dob_update, "YYYY-MM-DD"), "day")
    : 0;

  const isDobDisabled = dob_days < 180;

  const CustomPhoneInput = React.useMemo(() => {
    return React.forwardRef((props, ref) => (
      <input
        {...props}
        ref={ref}
        disabled={!selectedCountry}
        style={{
          backgroundColor: !selectedCountry ? '#f3f4f6' : 'white',
          cursor: !selectedCountry ? 'not-allowed' : 'text'
        }}
      />
    ));
  }, [selectedCountry]);

  const initialValues = {
    name: userData?.name,
    contact: userData?.phone 
      ? (String(userData.phone).startsWith('+') 
          ? userData.phone 
          : (userData?.phoneCode ? (String(userData.phoneCode).startsWith('+') ? '' : '+') + userData.phoneCode + userData.phone : userData.phone))
      : '',
    password: '',
    date_of_birth: userData?.dob
  };

  const togglePassword = () => {
    setNewPswShow(!newPswShow);
  }


  const adminPasswordSchema = Yup.object({
    name: Yup.string()
      .required('Full Name is required')
      .matches(/^[A-Za-z.\s]+$/, 'Only alphabets, dot and space are allowed')
      .min(5, 'Full Name must be 5 characters or more')
      .max(125, 'Full Name must be 125 characters or less'),
    contact: Yup.string().required("Contact is required"),
    date_of_birth: Yup.date()
      .required('Date of birth is required')
      .max(new Date(), 'Date of birth cannot be in the future')
      .typeError('Please enter a valid date')
      .test(
        'age',
        'You must be at least 18 years old',
        function (value) {
          if (!value) return false;

          const today = new Date();
          const birthDate = new Date(value);

          // Calculate the minimum allowed DOB
          const minDate = new Date(
            today.getFullYear() - 18,
            today.getMonth(),
          );

          return birthDate <= minDate;
        }
      ),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters (including uppercase, lowercase, number, and special character)")
      .matches(/[a-z]/, "At least one lowercase letter required")
      .matches(/[A-Z]/, "At least one uppercase letter required")
      .matches(/[0-9]/, "At least one number required")
      .matches(/[!@#$%^&*(),.?":{}|<>]/, "At least one special character required"),

  })
  const handleDateChange = (date) => {
    if (date && dayjs.isDayjs(date)) {
      const formattedDate = date.format('YYYY-MM-DD');
      setFieldValue('date_of_birth', formattedDate);
    } else {
      setFieldValue('date_of_birth', '');
    }
  };

  // Convert YYYY-MM-DD to DD/MM/YYYY for DatePicker display
  const getDatePickerValue = () => {
    if (values.date_of_birth) {
      const date = dayjs(values.date_of_birth, 'YYYY-MM-DD');
      if (date.isValid()) {
        return date.format('DD/MM/YYYY');
      }
    }
    return '';
  };

  const { values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue, resetForm } = useFormik({
    initialValues: initialValues,
    validationSchema: adminPasswordSchema,
    enableReinitialize: true,

    onSubmit: (values, action) => {
      setSpinner(true)
      dispatch(updateProfile(values)).then((res) => add_res(res.payload, action));
    }
  });

  const handleClose = () => {
    resetForm();
    setShow(false);
  };

  const add_res = (res, action) => {
    if (res?.status == 200) {
      setSpinner(false)
      toast.success(res?.data?.message, { position: 'bottom-right' })
      localStorage.setItem("name", values.name);
      localStorage.setItem("phone", values.contact);
      action.resetForm();
      setShow(false)
    } else {
      toast.error(res?.data?.message, { position: 'bottom-right' })
    }
  }

  useEffect(() => {
    if (show) {
      setLoading(true);
      dispatch(profileDetails({ navigate: navigate })).then((res) => {
        if (res?.payload?.status === 200) {
          setCurrentDate(res?.payload?.data?.currentDateTime);
          setLoading(false);
        } else {
          setLoading(false);
        }
      });
    }
  }, [show]);

  const popover = (
    <Popover id="popover-basic">
      <Popover.Body>
        Once you update Full Name and/or DOB, you can change anyone and/or both after 180 days
      </Popover.Body>
    </Popover>
  );

  return (
    <>
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Update Profile</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ?
            <div className='d-flex justify-content-center'>
              <Loader />
            </div> :

            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
              {/* <div className='mt-2 ps-3 pe-3'>
                <label htmlFor="user_name" className="form-label form_label">Contact</label>
                <input
                  type='number'
                  name='contact'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.contact}
                  className='form-control form_input' />
                {errors.contact && touched.contact ? (<span className='text-danger ' style={{ fontSize: "15px" }} >{errors.contact}</span>) : null}
              </div> */}
              <div className="mt-2 ps-3 pe-3">
                <div className='d-flex justify-content-between align-items-center'>
                  <label htmlFor="user_name" className="form-label form_label">Full Name</label>
                  <OverlayTrigger trigger="click" placement="top" overlay={popover}>
                    <Info size={20} className='mb-1' />
                  </OverlayTrigger>
                </div>
                <Form.Group controlId="name">
                  <Form.Control
                    disabled={userData?.last_dob_update ? isDobDisabled : false}
                    name='name'
                    value={values.name || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.name && touched.name ? (<span className='text-danger form_label'>{errors.name}</span>) : null}

                </Form.Group>


                {/* <Text
                  label_name={"Full Name"}
                  placeholder=''
                  disabled={userData?.last_dob_update ? isDobDisabled : false}
                  name='name'
                  value={values.name || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.name && touched.name ? (<span className='text-danger form_label'>{errors.name}</span>) : null}
                /> */}
              </div>
              <div className='mt-2 ps-3 pe-3'>
                <div className='d-flex justify-content-between align-items-center'>
                  <label htmlFor="user_name" className="form-label form_label">Date of Birth</label>
                  <OverlayTrigger trigger="click" placement="top" overlay={popover}>
                    <Info size={20} className='mb-1' />
                  </OverlayTrigger>
                </div>
                <DatePicker
                  label_name={""}
                  placeholder='DD/MM/YYYY'
                  name='date_of_birth'
                  isDobDisabled={userData?.last_dob_update ? isDobDisabled : false}
                  value={getDatePickerValue()}
                  onChange={handleDateChange}
                  onBlur={handleBlur}
                  error={errors.date_of_birth && touched.date_of_birth ? (<span className='text-danger form_label'>{errors.date_of_birth}</span>) : null}
                  mandatory={true}
                />
              </div>
              <div className="mt-2 ps-3 pe-3">
                <Form.Label className='form_label'>Contact Number</Form.Label>
                <PhoneInput
                  className={`form_input ${touched.contact && errors.contact ? 'is-invalid' : ''}`}
                  placeholder="Enter phone number"
                  international
                  addInternationalOption={true}
                  labels={{ ...labels, ZZ: 'Select Country Code' }}
                  name='contact'
                  value={values.contact || ''}
                  onCountryChange={setSelectedCountry}
                  onChange={(value) => setFieldValue('contact', value)}
                  onBlur={handleBlur}
                  inputComponent={CustomPhoneInput}
                />
                {touched.contact && errors.contact && (
                  <div className="text-danger form_label" style={{ fontSize: '14px', marginTop: '5px' }}>
                    {errors.contact}
                  </div>
                )}
                {values.contact && !isValidPhoneNumber(values.contact) && (
                  <div className="text-danger form_label" style={{ fontSize: '14px', marginTop: '5px' }}>
                    Invalid phone number
                  </div>
                )}
              </div>


              <div className='mt-2 ps-3 pe-3' style={{ position: 'relative' }}>
                <label htmlFor="user_name" className="form-label form_label">Password</label>
                {newPswShow ? <span className="psw_icon" onClick={togglePassword} ><BiShow size={20} style={{ color: "grey" }} /></span> : <span className="psw_icon" onClick={togglePassword} ><BiHide size={20} style={{ color: "#9d9d9d" }} /></span>}
                <input type={newPswShow ? "text" : "password"}
                  name='password'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.password}
                  className='form-control form_input' />
                {errors.password ? (<span className='text-danger' style={{ fontSize: "15px" }} >{errors.password}</span>) : null}
                <PasswordStrengthBar password={values.password} />
              </div>

              {/* <div className='mt-2 mb-4 ps-3 pe-3' style={{ position: 'relative' }}>
                <label htmlFor="user_name" className="form-label form_label">Conform Password</label>
                {confirmPswShow ? <span className="psw_icon" onClick={togglePassword1} ><BiShow size={20} style={{ color: "green" }} /></span> : <span className="psw_icon" onClick={togglePassword1} ><BiHide size={20} style={{ color: "red" }} /></span>}
                <input type={confirmPswShow ? "text" : "password"} onChange={handleChange} onBlur={handleBlur}
                  value={values.confirm_psw} name='confirm_psw' className='form-control form_input' />

                {errors.confirm_psw && touched.confirm_psw ? (<span className='text-danger ' style={{ fontSize: "15px" }} >{errors.confirm_psw}</span>) : null}
              </div> */}
              <div className='col-md-12 text-end mt-3'>
                <SaveButton button_name={'Update'} spinner={spinner} />
              </div>
            </form>
          }
        </Modal.Body>
      </Modal>
    </>
  )
}
